import React, { useState, useEffect } from 'react';

const StepIdentite = ({ formData, onChange }) => {
  // État local pour le champ "Depuis quand ?"
  const [showDepuisQuandField, setShowDepuisQuandField] = useState(
    ['Marié(e)', 'Pacsé(e)', 'Concubinage'].includes(formData.situationFamiliale)
  );
  
  // Déterminer la date maximale (aujourd'hui)
  const today = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD

  // Gérer le changement de date
  const handleDateChange = (e) => {
    console.log("Date changée:", e.target.value);
    onChange(e);
  };
  
  // Gérer la sélection de la situation familiale
  const handleSituationChange = (situation) => {
    onChange({ target: { name: 'situationFamiliale', value: situation } });
    setShowDepuisQuandField(['Marié(e)', 'Pacsé(e)', 'Concubinage'].includes(situation));
  };
  
  // Gérer le changement du nombre d'enfants
  const handleEnfantsChange = (value) => {
    // Assurer que la valeur est un nombre positif ou zéro
    const nombreEnfants = Math.max(0, parseInt(value) || 0);
    onChange({ target: { name: 'nombreEnfants', value: nombreEnfants.toString() } });
  };
  
  // Mettre à jour l'affichage du champ "Depuis quand ?" si la situation familiale change
  useEffect(() => {
    setShowDepuisQuandField(['Marié(e)', 'Pacsé(e)', 'Concubinage'].includes(formData.situationFamiliale));
  }, [formData.situationFamiliale]);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-orpi-secondary">Faisons connaissance</h2>
        <p className="text-gray-600">
          Assurez-vous que ces informations sont identiques à celles présentes sur votre pièce d'identité (carte d'identité, passeport...).
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">Civilité <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-4">
              <button 
                className={`p-3 border rounded-lg hover:border-orpi-primary ${
                  formData.civilite === 'Monsieur' ? 'border-orpi-primary bg-orpi-light' : ''
                }`}
                onClick={() => onChange({ target: { name: 'civilite', value: 'Monsieur' } })}
                type="button"
              >
                Monsieur
              </button>
              <button 
                className={`p-3 border rounded-lg hover:border-orpi-primary ${
                  formData.civilite === 'Madame' ? 'border-orpi-primary bg-orpi-light' : ''
                }`}
                onClick={() => onChange({ target: { name: 'civilite', value: 'Madame' } })}
                type="button"
              >
                Madame
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">Prénom <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="prenom"
              value={formData.prenom || ''}
              onChange={onChange}
              className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
              placeholder="Votre prénom"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">Nom <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="nom"
              value={formData.nom || ''}
              onChange={onChange}
              className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
              placeholder="Votre nom"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">Nom de naissance</label>
            <input
              type="text"
              name="nomNaissance"
              value={formData.nomNaissance || ''}
              onChange={onChange}
              className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
              placeholder="Votre nom de naissance"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">Date de naissance <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="dateNaissance"
              value={formData.dateNaissance || ''}
              onChange={handleDateChange}
              className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
              max={today}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Format : JJ/MM/AAAA (ex: 01/01/1980)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">Situation familiale <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 gap-4">
              {[
                'Célibataire',
                'Marié(e)',
                'Pacsé(e)',
                'Divorcé(e)',
                'Veuf(ve)',
                'Concubinage'
              ].map(situation => (
                <button
                  key={situation}
                  type="button"
                  className={`p-3 border rounded-lg hover:border-orpi-primary ${
                    formData.situationFamiliale === situation ? 'border-orpi-primary bg-orpi-light' : ''
                  }`}
                  onClick={() => handleSituationChange(situation)}
                >
                  {situation}
                </button>
              ))}
            </div>
          </div>
          
          {/* Nombre d'enfants à charge - uniquement la version avec + et - */}
          <div>
            <label className="block text-sm font-medium mb-1 text-orpi-secondary">Nombre d'enfants à charge</label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => handleEnfantsChange((parseInt(formData.nombreEnfants) || 0) - 1)}
                className="p-2 border rounded-l-lg hover:bg-gray-100"
                disabled={(parseInt(formData.nombreEnfants) || 0) <= 0}
              >
                <span className="text-xl">-</span>
              </button>
              <input
                type="number"
                name="nombreEnfants"
                value={formData.nombreEnfants || '0'}
                onChange={(e) => handleEnfantsChange(e.target.value)}
                min="0"
                className="w-16 p-3 border-t border-b text-center focus:border-orpi-primary focus:ring-orpi-primary"
              />
              <button
                type="button"
                onClick={() => handleEnfantsChange((parseInt(formData.nombreEnfants) || 0) + 1)}
                className="p-2 border rounded-r-lg hover:bg-gray-100"
              >
                <span className="text-xl">+</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Indiquez 0 si vous n'avez pas d'enfant à charge
            </p>
          </div>
          
          {/* Champ "Depuis quand ?" et message informatif (affichés conditionnellement) */}
          {showDepuisQuandField && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                <p>
                  <strong>Important :</strong> Si vous mentionnez un conjoint(e) dans votre dossier, celui-ci doit également remplir son propre formulaire de location de manière individuelle.
                </p>
                <p className="mt-2">
                  Cela permettra de regrouper les dossiers et de prendre en compte les revenus des deux parties pour l'étude complète de votre demande. Assurez-vous que chaque formulaire soit complété avec soin et que les informations fournies soient exactes. Tout retard ou omission pourrait entraîner un traitement différé de votre demande.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-orpi-secondary">Depuis quand ? <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="depuisQuandSituationFamiliale"
                  value={formData.depuisQuandSituationFamiliale || ''}
                  onChange={onChange}
                  className="w-full p-3 border rounded-lg focus:border-orpi-primary focus:ring-orpi-primary"
                  max={today}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Date de début de votre situation familiale actuelle
                </p>
              </div>
            </div>
          )}
        </div>
        
        <p className="text-sm text-gray-500">
          <span className="text-red-500">*</span> Champs obligatoires
        </p>
      </div>
    </div>
  );
};

export default StepIdentite;